import { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { DeliveryChannel, Order, OrderDetail, Patient, PatientSummary, Payment } from "@probetalab/contracts";
import { run } from "../lib/api.js";
import { useBranch, useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { ConditionPicker, PatientForm, PatientSearch, SelectedPatient } from "../components/PatientPicker.js";
import { DOCTOR_NONE, DoctorPicker, type DoctorChoice } from "../components/DoctorPicker.js";
import { computeDiscount, DiscountEditor, LinesList, NO_DISCOUNT, StudySearch, Totals, type DiscountState, type Line } from "../components/StudyPicker.js";
import { PaymentEditor, paymentsTotal, type PaymentDraft, type PendingPayment } from "../components/PaymentEditor.js";
import { OrderPrintActions } from "../components/PrintActions.js";
import { StudyDetailSheet, type StudyDetailItem } from "../components/StudyDetailSheet.js";
import { pendingTubesPayload, planTubes, TubeChecklist, withKey } from "../components/TubePlan.js";
import { Alert, Button, Card, Field, Input, PageHead, Pill, Segmented, Sheet, Textarea } from "../components/ui.js";
import { fullName, tubeName } from "../lib/format.js";

type Fasting = "unknown" | "yes" | "no";

/**
 * Recepción: one screen from "who is here" to the printed ticket and labels. Left column is the
 * person and the visit; right column is the order and the money, always visible with the live total.
 */
export function ReceptionPage() {
  const branch = useBranch();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state as { patient?: PatientSummary } | null)?.patient ?? null;

  const [patient, setPatient] = useState<PatientSummary | null>(preset);
  const [showNew, setShowNew] = useState(false);
  const [doctor, setDoctor] = useState<DoctorChoice>(DOCTOR_NONE);
  const [channels, setChannels] = useState<DeliveryChannel[]>(["whatsapp", "print"]);
  const [fasting, setFasting] = useState<Fasting>("unknown");
  const [fastingHours, setFastingHours] = useState("");
  const [condition, setCondition] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [discount, setDiscount] = useState<DiscountState>(NO_DISCOUNT);
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  // A typed but not yet «Agregar»-ed payment is saved too; an untouched line is not, because
  // registering without paying is allowed here.
  const [pending, setPending] = useState<PendingPayment | null>(null);
  const toSave = pending?.touched ? [...payments, pending.draft] : payments;
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<OrderDetail | null>(null);
  const [detail, setDetail] = useState<StudyDetailItem | null>(null);
  const studySearchRef = useRef<HTMLInputElement>(null);
  // Lab setting: tubes are taken at the counter, so they are registered as collected and the
  // receptionist marks only the missing one (keys of `planTubes`).
  const collectAtRegistration = useSession().me?.lab_settings.samples_collected_at_registration ?? false;
  const tubes = useMemo(() => (collectAtRegistration ? planTubes(lines) : []), [collectAtRegistration, lines]);
  const [pendingTubes, setPendingTubes] = useState<ReadonlySet<string>>(new Set());

  const subtotal = lines.reduce((s, l) => s + (l.amount_cents ?? 0), 0);
  const disc = computeDiscount(subtotal, discount);
  const total = Math.max(0, subtotal - disc.amount);
  const { paid, change } = paymentsTotal(payments);

  const fastingHoursNum = fastingHours === "" ? null : Math.max(0, Math.min(48, Math.round(Number(fastingHours) || 0)));
  const needsFasting = useMemo(() => lines.some((l) => /ayun/i.test(l.indications ?? "")), [lines]);
  const notFasting = fasting === "no" || (fastingHoursNum !== null && fastingHoursNum < 8);

  const canSave = patient && lines.length > 0 && channels.length > 0 && !saving;

  function toggleChannel(c: DeliveryChannel) {
    setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]));
  }

  async function save() {
    if (!patient || !lines.length) return;
    setSaving(true);
    try {
      const order = await run<"order.create", OrderDetail>(
        "order.create",
        {
          branch_id: branch.id,
          patient_id: patient.id,
          doctor_id: doctor.doctor_id,
          doctor_text: doctor.doctor_text,
          company_id: null,
          price_list_id: null,
          lines: lines.map((l) => ({ kind: l.kind, id: l.id, expected_amount_cents: l.amount_cents })),
          discount: disc.input,
          delivery_channels: channels,
          clinical_notes: notes.trim() || null,
          fasting_hours: fasting === "no" ? 0 : fastingHoursNum,
          condition: condition.trim() || null,
          catalogue_seen_at: null,
          pending_tubes: pendingTubesPayload(tubes, pendingTubes),
        },
        { branch_id: branch.id },
      );
      let latest = order;
      for (const p of toSave) {
        const r = await run<"payment.record", { payment: Payment; order: Order }>("payment.record", {
          order_id: order.id,
          branch_id: branch.id,
          method: p.method,
          amount_cents: p.amount_cents,
          reference: p.reference,
          tendered_cents: p.tendered_cents,
        });
        latest = { ...latest, balance_cents: r.order.balance_cents, paid_cents: r.order.paid_cents };
      }
      setSaved(latest);
      toast.success(`Solicitud ${order.folio} registrada.`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    setSaved(null);
    setPatient(null);
    setDoctor(DOCTOR_NONE);
    setChannels(["whatsapp", "print"]);
    setFasting("unknown");
    setFastingHours("");
    setCondition("");
    setNotes("");
    setLines([]);
    setPendingTubes(new Set());
    setDiscount(NO_DISCOUNT);
    setPayments([]);
  }

  return (
    <>
      <PageHead kicker={branch.name} title="Nueva solicitud" lede="Paciente, estudios y cobro en una sola pantalla. Al guardar se imprimen el ticket y las etiquetas." />

      <div className="split reception">
        <div className="stack-lg">
          <Card>
            <div className="section-title">
              <span className="step">1</span>
              <h2 className="h2">Paciente</h2>
            </div>
            {patient ? (
              <SelectedPatient patient={patient} onChange={() => setPatient(null)} />
            ) : (
              <div className="stack">
                <PatientSearch onPick={setPatient} autoFocus />
                <div className="row" style={{ justifyContent: "center" }}>
                  <Button variant="secondary" icon="userPlus" onClick={() => setShowNew(true)}>
                    Registrar paciente nuevo
                  </Button>
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div className="section-title">
              <span className="step">2</span>
              <h2 className="h2">Visita</h2>
            </div>
            <div className="stack-lg">
              <Field label="Médico solicitante">
                <DoctorPicker value={doctor} onChange={setDoctor} />
              </Field>
              <div className="grid-2">
                <Field label="Entrega de resultados">
                  <div className="pills">
                    <Pill on={channels.includes("whatsapp")} icon="whatsapp" onClick={() => toggleChannel("whatsapp")}>
                      WhatsApp
                    </Pill>
                    <Pill on={channels.includes("print")} icon="print" onClick={() => toggleChannel("print")}>
                      Impreso
                    </Pill>
                  </div>
                </Field>
                <Field label="Ayuno" hint={needsFasting ? "Hay estudios que piden ayuno de 8 a 12 horas." : undefined}>
                  <div className="row wrap">
                    <Segmented value={fasting} onChange={setFasting} options={[{ value: "unknown", label: "No aplica" }, { value: "yes", label: "Sí" }, { value: "no", label: "No" }]} />
                    {fasting === "yes" && (
                      <div className="input-wrap" style={{ width: 150 }}>
                        <span className="suffix">horas</span>
                        <Input inputMode="numeric" value={fastingHours} onChange={(e) => setFastingHours(e.target.value)} placeholder="8–12" aria-label="Horas de ayuno" />
                      </div>
                    )}
                  </div>
                </Field>
              </div>
              {needsFasting && notFasting && (
                <Alert tone="amber">
                  <strong>Sin ayuno suficiente.</strong> Los estudios que lo requieren deben reprogramarse; puedes registrar la solicitud y dejar la muestra pendiente.
                </Alert>
              )}
              <div className="grid-2">
                <Field label="Condición especial" hint="Selecciona los valores de referencia especiales (embarazo, por ejemplo).">
                  <ConditionPicker value={condition} onChange={setCondition} />
                </Field>
                <Field label="Notas clínicas" optional hint="Motivo del estudio, medicamentos o suplementos, semanas de gestación, fecha de última menstruación.">
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                </Field>
              </div>
            </div>
          </Card>
        </div>

        <div className="stack-lg">
          <Card>
            <div className="section-title">
              <span className="step">3</span>
              <h2 className="h2">Estudios</h2>
            </div>
            <div className="stack-lg">
              <StudySearch selected={lines} onPick={(it) => setLines((ls) => [...ls, it])} onInfo={setDetail} inputRef={studySearchRef} branchId={branch.id} />
              <LinesList lines={lines} onRemove={(l) => setLines((ls) => ls.filter((x) => !(x.kind === l.kind && x.id === l.id)))} onInfo={setDetail} />
              <TubeChecklist tubes={tubes} pending={pendingTubes} onToggle={(key, on) => setPendingTubes((p) => withKey(p, key, on))} />
              {lines.length > 0 && <DiscountEditor subtotal={subtotal} value={discount} onChange={setDiscount} />}
              <Totals subtotal={subtotal} discount={disc.amount} total={total} paid={payments.length ? paid : undefined} change={change} />
            </div>
          </Card>

          <Card>
            <div className="section-title">
              <span className="step">4</span>
              <h2 className="h2">Cobro</h2>
            </div>
            <PaymentEditor due={total} payments={payments} onChange={setPayments} onPending={setPending} onEnter={() => canSave && !saving && void save()} />
          </Card>

          <Button variant="primary" size="lg" block icon="print" onClick={() => void save()} disabled={!canSave} busy={saving}>
            Guardar e imprimir
          </Button>
          {!patient && lines.length > 0 && <p className="small muted" style={{ textAlign: "center" }}>Falta elegir al paciente.</p>}
        </div>
      </div>

      <StudyDetailSheet item={detail} onClose={() => setDetail(null)} returnFocus={studySearchRef} />

      <Sheet open={showNew} onClose={() => setShowNew(false)} title="Registrar paciente" wide>
        <PatientForm
          onSaved={(p: Patient) => {
            setPatient(p);
            setShowNew(false);
          }}
          onCancel={() => setShowNew(false)}
        />
      </Sheet>

      <Sheet
        open={saved !== null}
        onClose={() => {
          reset();
        }}
        title={saved ? `Solicitud ${saved.folio}` : ""}
        footer={
          saved && (
            <>
              <Button variant="ghost" onClick={() => navigate(`/solicitudes/${saved.id}`)}>
                Ver solicitud
              </Button>
              <Button variant="primary" icon="plus" onClick={reset}>
                Siguiente paciente
              </Button>
            </>
          )
        }
      >
        {saved && (
          <div className="stack-lg">
            <Alert tone="green">
              Registrada para <strong>{fullName(saved.patient)}</strong>. {saved.balance_cents > 0 ? `Adeudo ${(saved.balance_cents / 100).toLocaleString("es-MX", { style: "currency", currency: "MXN" })}.` : "Pagada por completo."}
            </Alert>
            <div className="panel">
              <div className="kicker" style={{ marginBottom: 8 }}>
                Tubos a etiquetar
              </div>
              <div className="row wrap" style={{ gap: 14 }}>
                {saved.samples.map((s) => (
                  <span key={s.id} className="tube">
                    <span className="tube-cap" style={{ background: s.container_colour_hex ?? "#ccc" }} />
                    <span>
                      <b>{tubeName(s)}</b> <span className="muted small">· {s.study_claves.join(", ")}</span>
                    </span>
                  </span>
                ))}
                {saved.samples.length === 0 && <span className="muted">Esta solicitud no requiere toma de muestra.</span>}
              </div>
            </div>
            <OrderPrintActions orderId={saved.id} samples={saved.samples} size="lg" autoPrint />
          </div>
        )}
      </Sheet>
    </>
  );
}
